import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { imobiliariaDataset, RealEstateProperty } from '../../shared/data/imobiliariaData';
import { Search, MapPin, BedDouble, Bath, Car, Maximize, Phone, ChevronRight, Menu, ArrowRight, ShieldCheck, Building, Filter, LayoutGrid, Map, Video, Calendar, Share2, ArrowLeft, CheckCircle2, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';

interface RealEstateDemoProps {
  prospect: Prospect;
}

export const RealEstateDemo: React.FC<RealEstateDemoProps> = ({ prospect }) => {
  const [activeCategory, setActiveCategory] = useState<'Todos' | 'Residencial' | 'Comercial' | 'Casa' | 'Apartamento' | 'Sala Comercial'>('Todos');
  const [viewMode, setViewMode] = useState<'lista' | 'mapa' | 'reels'>('lista');
  const [selectedProperty, setSelectedProperty] = useState<RealEstateProperty | null>(null);
  const [selectedVisitDate, setSelectedVisitDate] = useState<'HOJE' | 'AMANHÃ' | 'SEXTA' | 'SÁBADO'>('AMANHÃ');
  const [visitBooked, setVisitBooked] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const root = document.documentElement;
    root.style.setProperty('--ym-prospect-primary', '#1E3A8A');
    root.style.setProperty('--ym-prospect-accent', '#10B981');
  }, []);

  const formatPrice = (price?: number) => {
    if (!price) return 'Sob Consulta';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price);
  };

  const filteredProperties = activeCategory === 'Todos'
    ? imobiliariaDataset
    : imobiliariaDataset.filter(p => p.type === activeCategory || (activeCategory === 'Residencial' && (p.type === 'Apartamento' || p.type === 'Casa' || p.type === 'Cobertura')) || (activeCategory === 'Comercial' && p.type === 'Sala Comercial'));

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans selection:bg-[#1E3A8A] selection:text-white pb-32">
      
      {/* Header Mobg / Neutto Style Clean White - STRICT NO SHADOWS */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
             <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center font-black text-xl border border-blue-900">
                   <Building className="w-5 h-5" />
                </div>
                <div>
                   <h1 className="font-heading font-black text-xl tracking-tight uppercase text-[#0F172A] leading-none">{prospect.trade_name}</h1>
                   <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Imóveis & Lançamentos</span>
                </div>
             </div>

             <nav className="hidden md:flex items-center gap-6 text-xs font-bold text-slate-600 uppercase tracking-wider">
                <a href="#imoveis" className="hover:text-[#1E3A8A] transition-colors">Imóveis</a>
                <Link to={`/${prospect.slug}/hotpage`} className="hover:text-[#1E3A8A] transition-colors flex items-center gap-1 text-emerald-600">
                   ✨ Lançamento Ámbar (Macodesc)
                </Link>
                <a href="#servicos" className="hover:text-[#1E3A8A] transition-colors">Serviços</a>
                <a href="#sobre" className="hover:text-[#1E3A8A] transition-colors">Sobre</a>
             </nav>
          </div>

          <div className="flex items-center gap-3">
             <a href="#suporte" className="hidden lg:flex items-center gap-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-xl border border-slate-200 transition-colors">
                <Phone className="w-4 h-4 text-[#1E3A8A]" /> Suporte ao Aluguel
             </a>
             <button className="bg-[#1E3A8A] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-900 transition-colors border border-blue-950">
                Anuncie seu Imóvel
             </button>
          </div>
        </div>
      </header>

      {/* Barra de Filtros Rápidos (Pílulas Mobg Style) - STRICT NO SHADOWS */}
      <section className="bg-white border-b border-slate-200 py-4 px-6 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
           
           <div className="flex items-center gap-2 overflow-x-auto ym-hide-scrollbar">
              {[
                 { id: 'Todos', label: 'Todos' },
                 { id: 'Residencial', label: '🏡 Residencial' },
                 { id: 'Apartamento', label: '🏢 Apartamento' },
                 { id: 'Casa', label: '🏠 Casa' },
                 { id: 'Comercial', label: '🏬 Comercial' },
              ].map(cat => (
                 <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id as any)}
                    className={`px-4 py-2 rounded-2xl text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors border ${
                       activeCategory === cat.id 
                          ? 'bg-[#0F172A] text-white border-[#0F172A]' 
                          : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                    }`}
                 >
                    {cat.label}
                 </button>
              ))}
           </div>

           {/* Toggle Lista | Mapa | Reels */}
           <div className="flex items-center gap-1 bg-slate-100 p-1.5 rounded-2xl border border-slate-200 shrink-0">
              <button 
                 onClick={() => setViewMode('lista')}
                 className={`px-3.5 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${viewMode === 'lista' ? 'bg-white text-[#0F172A] border border-slate-300' : 'text-slate-500'}`}
              >
                 <LayoutGrid className="w-3.5 h-3.5" /> Lista
              </button>
              <button 
                 onClick={() => setViewMode('mapa')}
                 className={`px-3.5 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${viewMode === 'mapa' ? 'bg-white text-[#0F172A] border border-slate-300' : 'text-slate-500'}`}
              >
                 <Map className="w-3.5 h-3.5" /> Mapa
              </button>
              <button 
                 onClick={() => setViewMode('reels')}
                 className={`px-3.5 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${viewMode === 'reels' ? 'bg-white text-[#0F172A] border border-slate-300' : 'text-slate-500'}`}
              >
                 <Video className="w-3.5 h-3.5 text-rose-600" /> Reels
              </button>
           </div>

        </div>
      </section>

      {/* Banner Promocional Lançamento Ámbar - STRICT NO SHADOWS */}
      <section className="max-w-7xl mx-auto px-6 pt-6">
         <div className="bg-gradient-to-r from-slate-900 to-[#1E3A8A] text-white p-6 rounded-2xl border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2">
               <span className="bg-[#10B981] text-black text-[10px] font-black uppercase px-3 py-1 rounded-md">Exclusividade Macodesc</span>
               <h3 className="text-2xl font-heading font-black">Lançamento Residencial Ámbar — Chapecó/SC</h3>
               <p className="text-xs text-slate-300 font-light">Apartamentos de 3 Suítes (112 m² a 245 m²) no Centro. Cadastre-se para a Tabela de Pré-Lançamento.</p>
            </div>
            <Link to={`/${prospect.slug}/hotpage`} className="bg-white text-[#0F172A] px-6 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-200 transition-colors whitespace-nowrap border border-white">
               Conhecer Empreendimento →
            </Link>
         </div>
      </section>

      {/* Grid de Imóveis Mobg Style - STRICT NO SHADOWS */}
      <section id="imoveis" className="max-w-7xl mx-auto px-6 pt-8">
         <div className="flex justify-between items-center mb-6">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{filteredProperties.length} imóveis encontrados</span>
            <span className="text-xs text-slate-500">Ordenado por: <strong className="text-[#0F172A]">Mais Recentes</strong></span>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map(prop => (
               <div 
                  key={prop.id}
                  onClick={() => setSelectedProperty(prop)}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-[#1E3A8A] transition-colors cursor-pointer group flex flex-col justify-between p-4"
               >
                  <div>
                     {/* Photo Carousel Dots */}
                     <div className="relative aspect-[16/10] rounded-xl overflow-hidden mb-4 border border-slate-100">
                        <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        
                        {/* Status Badges Mobg Style */}
                        <div className="absolute top-3 left-3 flex gap-2">
                           <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md border border-emerald-300">
                              ✓ {prop.status}
                           </span>
                           {prop.isNew && (
                              <span className="bg-blue-100 text-blue-800 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md border border-blue-300">
                                 Novo
                              </span>
                           )}
                        </div>

                        <span className="absolute bottom-3 right-3 bg-black/70 text-white font-mono text-[10px] font-bold px-2 py-0.5 rounded">
                           {prop.code}
                        </span>
                     </div>

                     <h3 className="font-heading font-bold text-base text-[#0F172A] mb-1 group-hover:text-[#1E3A8A] transition-colors leading-snug">{prop.title}</h3>
                     <p className="text-xs text-slate-500 font-light mb-3">{prop.location.address} — Bairro {prop.location.neighborhood}, {prop.location.city}</p>

                     {/* Specs Pills Mobg Style */}
                     <div className="flex items-center gap-3 text-xs text-slate-600 font-medium mb-4">
                        <span>{prop.bedrooms} quartos</span> •
                        <span>{prop.bathrooms} banheiro</span> •
                        <span>{prop.parking} vaga</span> •
                        <span className="font-bold text-[#0F172A]">{prop.areaUtil} m²</span>
                     </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-end justify-between">
                     <div>
                        <span className="text-xl font-black text-[#0F172A]">
                           {formatPrice(prop.priceAluguel || prop.priceVenda)}
                        </span>
                        {prop.totalMonthlyCost && (
                           <span className="text-[11px] text-slate-500 block">
                              Total: <strong className="text-[#0F172A]">{formatPrice(prop.totalMonthlyCost)}/mês</strong>
                           </span>
                        )}
                     </div>

                     <button className="bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-[#0F172A] px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-colors border border-slate-200">
                        Ver Detalhes
                     </button>
                  </div>
               </div>
            ))}
         </div>
      </section>

      {/* Modal Interno Detalhado do Imóvel Mobg Style - STRICT NO SHADOWS */}
      {selectedProperty && (
         <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-300 rounded-2xl max-w-4xl w-full my-8 overflow-hidden relative">
               
               {/* Modal Header */}
               <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                  <div className="flex items-center gap-3">
                     <button onClick={() => setSelectedProperty(null)} className="p-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100">
                        <ArrowLeft className="w-4 h-4" />
                     </button>
                     <div>
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest font-mono">{selectedProperty.code} • {selectedProperty.location.neighborhood}, {selectedProperty.location.city}</span>
                        <h2 className="font-heading font-black text-lg text-[#0F172A]">{selectedProperty.title}</h2>
                     </div>
                  </div>
                  <button onClick={() => setSelectedProperty(null)} className="text-slate-400 hover:text-black font-bold">✕</button>
               </div>

               <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
                  
                  {/* Left Column: Gallery Grid & Info */}
                  <div className="lg:col-span-8 space-y-6">
                     
                     {/* Gallery Grid (1 big + 4 small) */}
                     <div className="grid grid-cols-3 gap-2 rounded-xl overflow-hidden border border-slate-200">
                        <img src={selectedProperty.images[0]} className="col-span-2 aspect-[4/3] object-cover w-full h-full" alt="Main" />
                        <div className="grid grid-rows-2 gap-2">
                           <img src={selectedProperty.images[1] || selectedProperty.images[0]} className="aspect-[4/3] object-cover w-full h-full" alt="2" />
                           <img src={selectedProperty.images[2] || selectedProperty.images[0]} className="aspect-[4/3] object-cover w-full h-full" alt="3" />
                        </div>
                     </div>

                     {/* Notice Box */}
                     <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                        <span className="text-xs font-bold text-emerald-900">
                           Este imóvel pode ser alugado no mesmo dia através da nossa garantia de fiança simplificada.
                        </span>
                     </div>

                     {/* Specs Cards Grid */}
                     <div className="grid grid-cols-4 gap-3">
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                           <span className="text-[10px] text-slate-400 font-bold uppercase block">Área Útil</span>
                           <span className="text-sm font-black text-[#0F172A]">{selectedProperty.areaUtil} m²</span>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                           <span className="text-[10px] text-slate-400 font-bold uppercase block">Quartos</span>
                           <span className="text-sm font-black text-[#0F172A]">{selectedProperty.bedrooms}</span>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                           <span className="text-[10px] text-slate-400 font-bold uppercase block">Banheiros</span>
                           <span className="text-sm font-black text-[#0F172A]">{selectedProperty.bathrooms}</span>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                           <span className="text-[10px] text-slate-400 font-bold uppercase block">Vagas</span>
                           <span className="text-sm font-black text-[#0F172A]">{selectedProperty.parking}</span>
                        </div>
                     </div>

                     {/* O que este imóvel oferece */}
                     <div>
                        <h4 className="font-heading font-black text-sm uppercase text-[#0F172A] mb-3">O que este imóvel oferece</h4>
                        <div className="flex flex-wrap gap-2">
                           {selectedProperty.amenities.map(a => (
                              <span key={a} className="bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-xl border border-slate-200">
                                 ✓ {a}
                              </span>
                           ))}
                        </div>
                     </div>

                  </div>

                  {/* Right Column: Pricing & Agendamento de Visitas Widget */}
                  <div className="lg:col-span-4 space-y-6">
                     
                     <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
                        <div>
                           <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block">Custos Mensais</span>
                           <h3 className="text-3xl font-black text-[#0F172A]">{formatPrice(selectedProperty.priceAluguel || selectedProperty.priceVenda)}<span className="text-xs text-slate-500 font-normal">/mês</span></h3>
                        </div>

                        {selectedProperty.totalMonthlyCost && (
                           <div className="space-y-2 text-xs pt-4 border-t border-slate-200 text-slate-600">
                              <div className="flex justify-between"><span>Aluguel:</span><span className="font-bold text-[#0F172A]">{formatPrice(selectedProperty.priceAluguel)}</span></div>
                              <div className="flex justify-between"><span>Condomínio:</span><span className="font-bold text-[#0F172A]">{formatPrice(selectedProperty.condoFee)}</span></div>
                              <div className="flex justify-between"><span>IPTU:</span><span className="font-bold text-[#0F172A]">{formatPrice(selectedProperty.iptuFee)}</span></div>
                              <div className="flex justify-between font-black text-sm pt-2 border-t border-slate-200 text-[#0F172A]">
                                 <span>Total Mensal:</span>
                                 <span className="text-emerald-700">{formatPrice(selectedProperty.totalMonthlyCost)}</span>
                              </div>
                           </div>
                        )}

                        {/* Agendar Visita Online */}
                        <div className="pt-4 border-t border-slate-200">
                           <h4 className="font-bold text-xs uppercase tracking-wider text-[#0F172A] mb-2">Agendar Visita com Corretor</h4>
                           <div className="grid grid-cols-2 gap-2 mb-3">
                              {(['HOJE', 'AMANHÃ', 'SEXTA', 'SÁBADO'] as const).map(dt => (
                                 <button
                                    key={dt}
                                    onClick={() => setSelectedVisitDate(dt)}
                                    className={`py-2 rounded-xl text-[10px] font-bold transition-colors border ${selectedVisitDate === dt ? 'bg-[#1E3A8A] text-white border-blue-900' : 'bg-white text-slate-700 border-slate-300'}`}
                                 >
                                    {dt}
                                 </button>
                              ))}
                           </div>

                           <button 
                              onClick={() => setVisitBooked(true)}
                              className="w-full bg-[#10B981] hover:bg-emerald-600 text-[#0F172A] py-3.5 rounded-xl font-black text-xs uppercase tracking-wider transition-colors border border-emerald-500"
                           >
                              {visitBooked ? '✓ Visita Confirmada!' : 'Confirmar Agendamento de Visita'}
                           </button>
                        </div>

                     </div>

                  </div>

               </div>

            </div>
         </div>
      )}

    </div>
  );
};
